import React, { useMemo, useState } from 'react';
import Papa from 'papaparse';

function ProductStockPage() {
  const [stockData, setStockData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedTag, setSelectedTag] = useState('all');
  const [sortOrder, setSortOrder] = useState('asc');
  const [sortField, setSortField] = useState('CurrentQuantity');
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRow, setExpandedRow] = useState(null);
  const itemsPerPage = 25;

  // Завантаження даних при монтуванні
  React.useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || `${window.location.protocol}//${window.location.hostname}:8000`;
        const response = await fetch(`${apiBaseUrl}/service/get_product_stock`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log(`✅ Завантажено ${data.length} залишків`);
        setStockData(data);
      } catch (error) {
        console.error('Error fetching product stock:', error);
        alert(`Помилка завантаження залишків: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const allCategories = useMemo(() => {
    const set = new Set(stockData.map(i => i.Category));
    return Array.from(set);
  }, [stockData]);

  const processedData = useMemo(() =>
    stockData.map(item => {
      let suppliersList = [];
      if (Array.isArray(item.Suppliers)) {
        suppliersList = item.Suppliers.filter(s => s && s.trim()).map(s => s.trim());
      } else if (typeof item.Suppliers === 'string' && item.Suppliers.trim()) {
        suppliersList = item.Suppliers.split(',').filter(s => s && s.trim()).map(s => s.trim());
      }
      
      if (suppliersList.length === 0) {
        suppliersList = ['Не вказано'];
      }
      
      const suppliersText = suppliersList.join(', ');
      
      return { 
        ...item, 
        suppliersList, 
        suppliersText
      };
    }), [stockData]);

  const filteredData = useMemo(() => {
    const searchMatch = search.trim().toLowerCase();
    let filtered = processedData.filter(item => {
      const matchProduct = item.Product.toLowerCase().includes(searchMatch) ||
        String(item.ProductID).includes(searchMatch);
      const matchCategory = !selectedCategory || item.Category === selectedCategory;
      
      // Визначаємо alertType на основі статусу
      let alertType = null;
      if (item.Status === 'deadstock') alertType = 'deadstock';
      else if (item.Status === 'red') alertType = 'critical';
      else if (item.Status === 'yellow') alertType = 'warning';
      
      const matchTag = selectedTag === 'all' || alertType === selectedTag;
      
      return matchProduct && matchCategory && matchTag;
    });

    filtered.sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [processedData, search, selectedCategory, selectedTag, sortField, sortOrder]);

  const totalPages = Math.max(1, Math.ceil(filteredData.length / itemsPerPage));
  const paginatedData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const toggleSort = field => {
    setSortField(field);
    setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const getRowColor = status =>
    status === 'deadstock' ? 'bg-gray-200' :
    status === 'red' ? 'bg-red-50' :
    status === 'yellow' ? 'bg-yellow-50' :
    status === 'green' ? 'bg-green-50' : '';

  const handleExport = () => {
    const csv = Papa.unparse(filteredData.map(item => ({
      ProductID: item.ProductID,
      Product: item.Product,
      Category: item.Category,
      CurrentQuantity: item.CurrentQuantity,
      QuantityAfter1Week: item.QuantityAfter1Week,
      QuantityAfter2Weeks: item.QuantityAfter2Weeks,
      Status: item.Status,
      Suppliers: item.suppliersText
    })));
    const blob = new Blob(["\uFEFF" + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'product_stock.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="p-4">
      <h2 className="text-lg font-semibold mb-3">Залишки продуктів</h2>
      <div className="flex gap-2 mb-3 flex-wrap sm:flex-nowrap">
        <input
          type="text"
          placeholder="Пошук..."
          className="border px-2 py-1 rounded text-xs w-36"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          value={selectedCategory}
          onChange={e => setSelectedCategory(e.target.value)}
          className="border px-2 py-1 rounded text-xs w-36"
        >
          <option value="">Усі категорії</option>
          {allCategories.sort().map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
        <button onClick={handleExport} className="bg-blue-500 text-white px-3 py-1 text-xs rounded">Завантажити CSV</button>
      </div>
      <div className="flex gap-2 mb-3">
        {['all', 'critical', 'warning', 'deadstock'].map(tag => (
          <button
            key={tag}
            onClick={() => setSelectedTag(tag)}
            className={`text-xs px-3 py-1 rounded ${selectedTag === tag ? (tag === 'critical' ? 'bg-red-500 text-white' : tag === 'warning' ? 'bg-yellow-500 text-white' : tag === 'deadstock' ? 'bg-gray-600 text-white' : 'bg-blue-500 text-white') : 'bg-gray-200'}`}
          >
            {tag === 'all' ? 'Всі' : tag === 'critical' ? 'Критичні' : tag === 'warning' ? 'Увага' : 'Запаси без руху'}
          </button>
        ))}
      </div>

      {!loading && (
        <>
          <table className="table-auto w-full text-xs border border-gray-300">
            <thead className="bg-gray-100">
              <tr>
                <th onClick={()=>toggleSort('ProductID')} className="py-1 px-1 border cursor-pointer">ID ↑↓</th>
                <th onClick={()=>toggleSort('Product')} className="py-1 px-1 border cursor-pointer">Продукт ↑↓</th>
                <th onClick={()=>toggleSort('CurrentQuantity')} className="py-1 px-1 border text-center cursor-pointer">Залишки ↑↓</th>
                <th onClick={()=>toggleSort('QuantityAfter1Week')} className="py-1 px-1 border text-center cursor-pointer">+1 Тиж. ↑↓</th>
                <th onClick={()=>toggleSort('QuantityAfter2Weeks')} className="py-1 px-1 border text-center cursor-pointer">+2 Тижні ↑↓</th>
                <th onClick={()=>toggleSort('Category')} className="py-1 px-1 border cursor-pointer">Категорія ↑↓</th>
                <th onClick={()=>toggleSort('suppliersText')} className="py-1 px-1 border cursor-pointer">Постачальник ↑↓</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((item, idx)=>(
                <React.Fragment key={idx}>
                  <tr onClick={()=>setExpandedRow(expandedRow===idx?null:idx)} className={`border-t cursor-pointer hover:bg-gray-50 ${getRowColor(item.Status)}`}>
                    <td className="py-1 px-1 border">{item.ProductID}</td>
                    <td className="py-1 px-1 border whitespace-normal break-words">{item.Product}</td>
                    <td className="py-1 px-1 border text-center">{Math.round(item.CurrentQuantity)}</td>
                    <td className="py-1 px-1 border text-center">{item.QuantityAfter1Week !== undefined ? Math.round(item.QuantityAfter1Week) : '-'}</td>
                    <td className="py-1 px-1 border text-center">{item.QuantityAfter2Weeks !== undefined ? Math.round(item.QuantityAfter2Weeks) : '-'}</td>
                    <td className="py-1 px-1 border">{item.Category}</td>
                    <td className="py-1 px-1 border truncate max-w-[140px]">{item.suppliersText}</td>
                  </tr>
                  {expandedRow===idx && (
                    <tr className="bg-blue-50">
                      <td colSpan="7" className="px-2 py-2 text-xs text-gray-700">
                        <strong>Постачальник:</strong> {item.suppliersText}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>

          <div className="flex justify-between items-center mt-4 text-xs">
            <button onClick={()=>setCurrentPage(p=>Math.max(1,p-1))} disabled={currentPage===1} className="bg-gray-200 px-2 py-1 rounded disabled:opacity-50">← Попередня</button>
            <div className="flex items-center gap-2">
              <span>Сторінка</span>
              <input
                type="number"
                value={currentPage}
                min={1}
                max={totalPages}
                onChange={e=>setCurrentPage(Math.min(totalPages,Math.max(1,parseInt(e.target.value)||1)))}
                className="border rounded px-2 py-1 w-12 text-center"
              />
              <span>з {totalPages}</span>
            </div>
            <button onClick={()=>setCurrentPage(p=>Math.min(p+1,totalPages))} disabled={currentPage===totalPages} className="bg-gray-200 px-2 py-1 rounded disabled:opacity-50">Наступна →</button>
          </div>
        </>)}
    </div>
  );
}

export default ProductStockPage;

